import { Input } from 'components/custom/input';
import { SETTLEMENT_OPTIONS, translateLabel } from 'constants/format';
import { useTranslate } from 'context/TranslateContext';
import { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import { Modal, ModalBody, ModalHeader, Spinner } from 'reactstrap';
import { DefaultResponse } from 'services/API/common/Fetcher';
import { activateAutoSettlement, deactivateAutoSettlement } from 'services/API/Company';
import { useSwal } from 'helpers/sweetalert';
import { useSelector } from 'react-redux';
import { GlobalTypes } from 'redux/types';
import { useCurrency } from 'helpers/hooks/useCurrency';

interface SettlementSettingModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  currentSettlementType: boolean;
  company_id: string;
  reloadUser: () => Promise<void>;
}

export const SettlementSettingModal = ({
  isOpen,
  setIsOpen,
  currentSettlementType,
  company_id,
  reloadUser,
}: SettlementSettingModalProps) => {
  const {
    auth: { user },
  } = useSelector((state: GlobalTypes.RootState) => state);
  const initialState = {
    currency: '',
    active: false,
  };
  const [, , reactSelectCurrencyOptions] = useCurrency();

  const [form, setForm] = useState<{ currency: string; active: boolean }>(initialState);
  const [isLoading, setIsLoading] = useState(false);
  const [Swal] = useSwal();
  const { translate } = useTranslate();

  const { currency, active } = form;
  const settlements = user.company.auto_settlements || [];

  const onFieldChange = (name: string) => (val: string) => {
    if (name === 'currency') {
      const selectedCurrency = val;
      const settlement = settlements.find((s) => s.currency === selectedCurrency);
      return setForm({
        currency: selectedCurrency,
        active: settlement?.active || false,
      });
    }

    setForm({
      ...form,
      [name]: val,
    });
  };

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    let apiCall: () => Promise<DefaultResponse>;

    const body = { currency };
    if (active) {
      apiCall = () => activateAutoSettlement(company_id, body);
    } else {
      apiCall = () => deactivateAutoSettlement(company_id, body);
    }
    setIsLoading(true);
    const res = await apiCall();
    const isSuccess = res.success;

    await Swal.fire({
      icon: isSuccess ? 'success' : 'error',
      text: res?.message,
      title: translate('auto_settlement'),
    });
    await reloadUser();
    setIsLoading(false);
    setIsOpen(false);
  };

  const toggle = () => {
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    setForm(initialState);

    return () => {
      setForm(initialState);
    };
  }, [isOpen]);

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered>
      <ModalHeader toggle={toggle}>{translate('settlement_setting')}</ModalHeader>
      <ModalBody>
        <form onSubmit={onSubmit}>
          <Input
            legend={translate('currency').toUpperCase()}
            name="currency"
            value={currency}
            onChange={onFieldChange('currency')}
            options={reactSelectCurrencyOptions}
            defaultLabel={translate('select')}
            type="react-select"
            isRequired
            // error={translate(...(errors.currency ?? ''))}
          />
          {currency && (
            <Input
              type="react-select"
              legend={translate('auto_settlement').toUpperCase()}
              name="auto_settlement"
              value={active}
              onChange={onFieldChange('active')}
              options={translateLabel(SETTLEMENT_OPTIONS, translate, true)}
            />
          )}
          <div className="d-grid">
            <button className="btn btn-primary mt-3" disabled={isLoading}>
              {isLoading ? <Spinner size="sm" /> : translate('submit')}
            </button>
          </div>
        </form>
      </ModalBody>
    </Modal>
  );
};
